import { useEffect, useMemo, useState } from "react";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import { SubmitButton } from "#/components/shared/buttons/submit-button";
import Input from "#/components/shared/input";
import ThemedSelect from "#/components/shared/themed-select";
import Typography from "#/components/shared/typography";
import {
  MediaGenerateRequest,
  MediaUploadContext,
  MediaUploadContexts,
} from "#/api/mediaReference.api";
import { useGenerateMedia } from "#/features/media/hooks/useGenerateMedia";
import { useMediaGenerateStatus } from "#/features/media/hooks/useMediaGenerateStatus";
import { useMediaSearch } from "#/features/media/hooks/useMediaSearch";
import { buildMediaUrl } from "#/lib/mediaUrls";
import { MediaItem } from "#/lib/mediaApi";
import { useQueryClient } from "@tanstack/react-query";

const BLANK_PNG =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=";

type MediaGenerateFormProps = {
  onComplete?: () => void;
  initialItem?: MediaItem | null;
  initialContext?: MediaUploadContext;
  initialSourceMode?: SourceMode;
};

type SourceMode = "existing" | "upload" | "text";

export function MediaGenerateForm({
  onComplete,
  initialItem,
  initialContext,
  initialSourceMode,
}: MediaGenerateFormProps) {
  const queryClient = useQueryClient();
  const { mutateAsync: queueGenerate, isPending: isQueuing } =
    useGenerateMedia();
  const [prompt, setPrompt] = useState("");
  const [context, setContext] = useState<MediaUploadContext>(
    initialContext ?? "content"
  );
  const [fileName, setFileName] = useState("generated.png");
  const [model, setModel] = useState("gemini-2.5-flash-image");
  const [sourceMode, setSourceMode] = useState<SourceMode>(
    initialSourceMode ?? "existing"
  );
  const [selectedItem, setSelectedItem] = useState<MediaItem | null>(
    initialItem ?? null
  );
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageMimeType, setImageMimeType] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [pollStatus, setPollStatus] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const contextOptions = useMemo(
    () =>
      MediaUploadContexts.map((value) => ({
        value,
        label: value.replace(/\b\w/g, (char) => char.toUpperCase()),
      })),
    []
  );

  const { data: mediaResults, isLoading } = useMediaSearch({
    context,
    q: debouncedSearch || undefined,
    page: 1,
    limit: 8,
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 500);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (uploadFile) {
      setFileName(uploadFile.name);
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          setImageBase64(reader.result);
        }
      };
      reader.readAsDataURL(uploadFile);
      setImageMimeType(uploadFile.type || "image/png");
    } else {
      setImageBase64(null);
      setImageMimeType(null);
    }
  }, [uploadFile]);

  useEffect(() => {
    if (selectedItem?.url && sourceMode === "existing") {
      const fallbackName =
        selectedItem.slug?.split("/").pop() || "generated.png";
      setFileName(fallbackName);
    }
  }, [selectedItem, sourceMode]);

  useEffect(() => {
    if (initialItem) {
      setSelectedItem(initialItem);
      setSourceMode(initialSourceMode ?? "existing");
    }
    if (initialContext) {
      setContext(initialContext);
    }
  }, [initialItem, initialContext, initialSourceMode]);

  const { data: status } = useMediaGenerateStatus(requestId, pollStatus);

  useEffect(() => {
    if (!status?.status) return;
    if (status.status === "succeeded" || status.status === "failed") {
      setPollStatus(false);
      if (status.status === "succeeded") {
        queryClient.invalidateQueries({ queryKey: ["media", "search"] });
        onComplete?.();
      }
    }
  }, [status?.status, queryClient, onComplete]);

  const handleGenerate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!prompt.trim()) return;

    const payload: MediaGenerateRequest = {
      prompt: prompt.trim(),
      context,
      fileName: fileName.trim() || "generated.png",
      model: model.trim() || undefined,
    };

    if (sourceMode === "existing") {
      if (!selectedItem?.url) return;
      payload.imageUrl = selectedItem.url;
    } else if (sourceMode === "upload") {
      if (!imageBase64 || !imageMimeType) return;
      payload.imageBase64 = imageBase64;
      payload.imageMimeType = imageMimeType;
    } else {
      payload.imageBase64 = BLANK_PNG;
      payload.imageMimeType = "image/png";
    }

    const result = await queueGenerate(payload);
    setRequestId(result.request_id);
    setPollStatus(true);
  };

  const renderResultRow = (item: MediaItem) => {
    const url = buildMediaUrl(item.url ?? item.slug ?? "");
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => setSelectedItem(item)}
        className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm transition ${
          selectedItem?.id === item.id
            ? "border-terracotta-300 bg-cream-50"
            : "border-cream-200 hover:border-terracotta-200"
        }`}
      >
        <div className="h-10 w-12 overflow-hidden rounded-md bg-cream-100">
          {url ? (
            <img src={url} alt={item.name ?? item.slug ?? "Media"} />
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-brown-800">
            {item.name ?? item.slug ?? "Untitled"}
          </div>
          <div className="truncate text-xs text-brown-500">
            {item.slug ?? item.url ?? ""}
          </div>
        </div>
      </button>
    );
  };

  const statusLabel = status?.status
    ? status.status === "queued"
      ? "Queued"
      : status.status === "running"
      ? "Generating..."
      : status.status === "succeeded"
      ? "Succeeded"
      : "Failed"
    : null;

  return (
    <form onSubmit={handleGenerate} className="space-y-4">
      <Input
        id="generate-prompt"
        label="Prompt"
        value={prompt}
        onChange={(event) => setPrompt(event.target.value)}
        placeholder="Describe how you want to transform the image"
        fullWidth
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <ThemedSelect
          label="Context"
          options={contextOptions}
          initialValue={context}
          onChange={(event) =>
            setContext(event.target.value as MediaUploadContext)
          }
        />
        <Input
          id="generate-file-name"
          label="File name"
          value={fileName}
          onChange={(event) => setFileName(event.target.value)}
          placeholder="hero-variant.png"
          fullWidth
        />
      </div>
      <Input
        id="generate-model"
        label="Model"
        value={model}
        onChange={(event) => setModel(event.target.value)}
        placeholder="gemini-2.5-flash-image"
        fullWidth
      />

      <div className="space-y-2">
        <Typography variant="caption" className="text-brown-600">
          Image source
        </Typography>
        <div className="flex flex-wrap gap-2">
          <ActionButton
            type="button"
            tone={sourceMode === "existing" ? "primary" : "secondary"}
            onClick={() => setSourceMode("existing")}
          >
            Use existing image
          </ActionButton>
          <ActionButton
            type="button"
            tone={sourceMode === "upload" ? "primary" : "secondary"}
            onClick={() => setSourceMode("upload")}
          >
            Upload image
          </ActionButton>
          <ActionButton
            type="button"
            tone={sourceMode === "text" ? "primary" : "secondary"}
            onClick={() => setSourceMode("text")}
          >
            Text only
          </ActionButton>
        </div>
      </div>

      {sourceMode === "existing" ? (
        <div className="space-y-3">
          <Input
            id="media-search-select"
            label="Find existing image"
            placeholder="Search media..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            fullWidth
          />
          <div className="grid gap-2">
            {isLoading ? (
              <Typography variant="caption" className="text-brown-600">
                Loading images...
              </Typography>
            ) : mediaResults.length ? (
              mediaResults.map(renderResultRow)
            ) : (
              <Typography variant="caption" className="text-brown-600">
                No images found.
              </Typography>
            )}
          </div>
        </div>
      ) : sourceMode === "upload" ? (
        <Input
          id="media-generate-upload"
          label="Upload image"
          type="file"
          accept="image/*"
          onChange={(event) => setUploadFile(event.target.files?.[0] ?? null)}
          fullWidth
        />
      ) : (
        <Typography variant="caption" className="text-brown-600">
          Text-only generation uses a blank base image.
        </Typography>
      )}

      {statusLabel ? (
        <div className="rounded-lg border border-cream-200 bg-cream-50 p-3">
          <Typography variant="body2" className="text-brown-700">
            Status: {statusLabel}
          </Typography>
          {status?.cdn_url ? (
            <a
              href={status.cdn_url}
              target="_blank"
              rel="noreferrer"
              className="mt-1 block break-all text-sm text-terracotta-600 underline underline-offset-2"
            >
              {status.cdn_url}
            </a>
          ) : null}
          {status?.media_reference_id ? (
            <Typography variant="caption" className="text-brown-600">
              Media ID: {status.media_reference_id}
            </Typography>
          ) : null}
          {status?.error ? (
            <Typography variant="caption" className="text-red-600">
              {status.error}
            </Typography>
          ) : null}
        </div>
      ) : null}

      <SubmitButton
        isDisabled={
          isQueuing ||
          !prompt.trim() ||
          (sourceMode === "existing" && !selectedItem?.url) ||
          (sourceMode === "upload" && !imageBase64)
        }
        isLoading={isQueuing}
      >
        Generate Image
      </SubmitButton>
    </form>
  );
}
